import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { ModelMaticLoader } from '@/components/common'
import { LoggedOutOverlay } from '@/components/LoggedOutOverlay'
import { MaintenanceBanner } from '@/components/MaintenanceBanner'
import { MaintenanceOverlay } from '@/components/MaintenanceOverlay'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ProductTourShepherd } from '@/components/product-tour/ProductTourShepherd'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { WhatsNewHost } from '@/components/whats-new/WhatsNewDialog'
import {
	AuthProvider,
	DataRefreshProvider,
	MaintenanceProvider,
	SocketProvider,
	useAuth,
	WorkspaceProvider,
} from '@/context'
import { ProductTourProvider } from '@/context/ProductTourContext'
import { useFeatureRollout } from '@/hooks/useFeatureRollout'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { FEATURE_ROLLOUT_KEYS } from '@/types/featureRollout'

const AcceptTermsOnly = lazy(() => import('@/pages/AcceptTermsOnly'))
const AIAgent = lazy(() => import('@/pages/AIAgent'))
const AnalysisDetail = lazy(() => import('@/pages/AnalysisDetail'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const Help = lazy(() => import('@/pages/Help'))
const Login = lazy(() => import('@/pages/Login'))
const Onboarding = lazy(() => import('@/pages/Onboarding'))
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'))
const Projects = lazy(() => import('@/pages/Projects'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))
const Settings = lazy(() => import('@/pages/Settings'))
const SSOCallback = lazy(() => import('@/pages/SSOCallback'))
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'))
const DevInterviewPage = lazy(() => import('@/pages/DevInterview'))

function GatedDevInterview() {
	const { enabled, isLoading } = useFeatureRollout(FEATURE_ROLLOUT_KEYS.INTERVIEW_AGENT)

	if (isLoading) {
		return <ModelMaticLoader />
	}

	if (!enabled) {
		return <Navigate to="/projects" replace />
	}

	return (
		<Suspense fallback={<ModelMaticLoader />}>
			<DevInterviewPage />
		</Suspense>
	)
}

function AppShell() {
	return (
		<>
			<ProductTourShepherd />
			<div className="flex h-dvh flex-col overflow-hidden">
				<MaintenanceBanner />
				<div className="App flex min-h-0 flex-1 flex-col overflow-hidden">
					<Suspense fallback={<ModelMaticLoader />}>
						<Outlet />
					</Suspense>
				</div>
				<LoggedOutOverlay />
				<MaintenanceOverlay />
			</div>
		</>
	)
}

const router = createBrowserRouter([
	{
		element: <AppShell />,
		children: [
			{ path: '/login', element: <Login /> },
			{ path: '/forgot-password', element: <ForgotPassword /> },
			{ path: '/reset-password', element: <ResetPassword /> },
			{ path: '/verify-email', element: <VerifyEmail /> },
			{ path: '/sso/callback', element: <SSOCallback /> },
			{
				path: '/onboarding',
				element: (
					<ProtectedRoute>
						<Onboarding />
					</ProtectedRoute>
				),
			},
			{
				path: '/accept-terms',
				element: (
					<ProtectedRoute>
						<AcceptTermsOnly />
					</ProtectedRoute>
				),
			},
			{
				path: '/dashboard',
				element: (
					<ProtectedRoute>
						<Dashboard />
					</ProtectedRoute>
				),
			},
			{
				path: '/projects',
				element: (
					<ProtectedRoute>
						<Projects />
					</ProtectedRoute>
				),
			},
			{
				path: '/projects/:projectId/artifacts/:artifactId',
				element: (
					<ProtectedRoute>
						<ProjectDetail />
					</ProtectedRoute>
				),
			},
			{
				path: '/projects/:projectId',
				element: (
					<ProtectedRoute>
						<ProjectDetail />
					</ProtectedRoute>
				),
			},
			{
				path: '/projects/:projectId/analyses/:analysisId',
				element: (
					<ProtectedRoute>
						<AnalysisDetail />
					</ProtectedRoute>
				),
			},
			{
				path: '/settings',
				element: (
					<ProtectedRoute>
						<Settings />
					</ProtectedRoute>
				),
			},
			{
				path: '/ai-agent',
				element: (
					<ProtectedRoute>
						<AIAgent />
					</ProtectedRoute>
				),
			},
			{
				path: '/help',
				element: (
					<ProtectedRoute>
						<Help />
					</ProtectedRoute>
				),
			},
			{
				path: '/dev/interview',
				element: (
					<ProtectedRoute>
						<GatedDevInterview />
					</ProtectedRoute>
				),
			},
			{ path: '*', element: <Navigate to="/projects" replace /> },
		],
	},
])

function VersionChecker({ children }: { children: React.ReactNode }) {
	useVersionCheck()
	return <>{children}</>
}

function AppContent() {
	const { loading } = useAuth()

	if (loading) {
		return <ModelMaticLoader />
	}

	return (
		<ProductTourProvider>
			<VersionChecker>
				<WhatsNewHost />
				<RouterProvider router={router} />
			</VersionChecker>
		</ProductTourProvider>
	)
}

function App() {
	return (
		<MaintenanceProvider>
			<AuthProvider>
				<WorkspaceProvider>
					<DataRefreshProvider>
						<SocketProvider>
							<TooltipProvider delayDuration={0}>
								<AppContent />
								<Toaster position="top-right" closeButton />
							</TooltipProvider>
						</SocketProvider>
					</DataRefreshProvider>
				</WorkspaceProvider>
			</AuthProvider>
		</MaintenanceProvider>
	)
}

export default App
